import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  FormSection,
  Checkbox,
  LoadingSpinner,
  DataTable,
  Select,
} from '@airclou/ui'
import { dashboardApiClient } from '../../../services/api'
import { CheckCircle2, Monitor, Trash2 } from 'lucide-react'
import { ErrorState } from '../../../components/state/ErrorState'
import { ConfirmPasswordModal } from '../../../components/auth/ConfirmPasswordModal'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeInTz } from '@airclou/utils'

interface LoginSecuritySettings {
  email_on_new_login: boolean
  require_mfa_for_sensitive: boolean
  trust_device_days: number
}

interface TrustedDevice {
  id: string
  device_hash: string
  user_agent: string | null
  device_info: any
  first_seen_at: string
  last_seen_at: string
  trusted_until: string | null
}

export function LoginSecurityTab() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const [settings, setSettings] = useState<LoginSecuritySettings | null>(null)
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [_, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [devices, setDevices] = useState<TrustedDevice[]>([])
  const [loadingDevices, setLoadingDevices] = useState(true)
  const [devicesError, setDevicesError] = useState<string | null>(null)
  const [revokingHash, setRevokingHash] = useState<string | null>(null)

  // Confirm-password modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmModalError, setConfirmModalError] = useState<string | null>(null)
  const [pendingUpdate, setPendingUpdate] = useState<Partial<LoginSecuritySettings> | null>(null)
  const [preUpdateSettings, setPreUpdateSettings] = useState<LoginSecuritySettings | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  useEffect(() => {
    loadDevices()
  }, [])

  const loadSettings = async () => {
    setLoadingSettings(true)
    try {
      setError(null)
      const r = await dashboardApiClient.getLoginSecuritySettings()
      setSettings(r)
    } catch (e: any) {
      setError(e.message || t('security.failedToLoadSettings'))
    } finally {
      setLoadingSettings(false)
    }
  }

  const loadDevices = async () => {
    setLoadingDevices(true)
    try {
      setDevicesError(null)
      const r = await dashboardApiClient.getTrustedDevices()
      setDevices(r)
    } catch (e: any) {
      setDevicesError(e.message || t('security.failedToLoadDevices'))
    } finally {
      setLoadingDevices(false)
    }
  }

  const performUpdate = useCallback(async (
    payload: Partial<LoginSecuritySettings>,
    confirmPassword?: string
  ): Promise<boolean> => {
    try {
      const r = await dashboardApiClient.updateLoginSecuritySettings(payload, confirmPassword)
      setSettings(r)
      return true
    } catch (e: any) {
      if (e.code === 'SENSITIVE_ACTION_REQUIRES_CONFIRMATION') {
        setPendingUpdate(payload)
        setPreUpdateSettings(settings)
        setConfirmModalError(null)
        setConfirmModalOpen(true)
        return false
      }
      if (e.code === 'INVALID_CONFIRMATION_PASSWORD') {
        setConfirmModalError(t('security.incorrectPassword'))
        return false
      }
      throw e
    }
  }, [settings])

  const handleConfirmPassword = useCallback(async (password: string) => {
    if (!pendingUpdate) return
    const success = await performUpdate(pendingUpdate, password)
    if (success) {
      setConfirmModalOpen(false)
      setPendingUpdate(null)
      setPreUpdateSettings(null)
      setConfirmModalError(null)
    }
  }, [pendingUpdate, performUpdate])

  const handleConfirmModalClose = useCallback(() => {
    setConfirmModalOpen(false)
    setConfirmModalError(null)
    // Revert optimistic update
    if (preUpdateSettings) {
      setSettings(preUpdateSettings)
    }
    setPendingUpdate(null)
    setPreUpdateSettings(null)
  }, [preUpdateSettings])

  const handleToggle = async (field: keyof LoginSecuritySettings) => {
    if (!settings) return
    const next = { ...settings, [field]: !settings[field] as any }
    setSettings(next)
    setSaving(true)
    try {
      await performUpdate({ [field]: (next as any)[field] })
    } catch (e) {
      // revert
      setSettings(settings)
    } finally {
      setSaving(false)
    }
  }

  const columns = useMemo(() => [
    {
      id: 'device',
      header: t('security.device'),
      cell: (row: TrustedDevice) => (
        <div className="flex items-center gap-2">
          <Monitor className="h-4 w-4 text-muted-foreground" />
          <div className="truncate max-w-[360px]" title={row.user_agent || ''}>
            {row.device_info?.browser || 'Unknown'} on {row.device_info?.os || 'Unknown'}
          </div>
        </div>
      ),
      size: 340,
      isFlexible: true,
    },
    {
      id: 'first_seen',
      header: t('security.firstSeen'),
      cell: (row: TrustedDevice) => formatDateTimeInTz(row.first_seen_at, timezone),
      size: 180,
    },
    {
      id: 'last_seen',
      header: t('security.lastSeen'),
      cell: (row: TrustedDevice) => formatDateTimeInTz(row.last_seen_at, timezone),
      size: 180,
    },
    {
      id: 'trusted_until',
      header: t('security.trustedUntil'),
      cell: (row: TrustedDevice) => row.trusted_until ? formatDateTimeInTz(row.trusted_until, timezone) : t('security.notTrusted'),
      size: 180,
    },
    {
      id: 'actions',
      header: '',
      cell: (row: TrustedDevice) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleRevoke(row.device_hash)}
          disabled={revokingHash === row.device_hash}
        >
          {revokingHash === row.device_hash ? (
            <LoadingSpinner size="small" />
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" /> {t('security.revoke')}
            </>
          )}
        </Button>
      ),
      size: 140,
    }
  ], [revokingHash, timezone])

  const handleRevoke = async (deviceHash: string) => {
    setRevokingHash(deviceHash)
    try {
      await dashboardApiClient.deleteTrustedDevice(deviceHash)
      setDevices(prev => prev.filter(d => d.device_hash !== deviceHash))
    } catch (e) {
      // noop, ErrorState shows elsewhere
    } finally {
      setRevokingHash(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* The tab itself is uncapped so the trusted-devices table below can use
          its full column widths. These toggle rows are an ordinary form and
          keep the standard cap, or the control on the right drifts a screen
          away from the label on the left. */}
      <FormSection
        title={t('security.loginSecurity')}
        className="max-w-2xl"
      >
        {loadingSettings ? (
          <div className="py-6"><LoadingSpinner /></div>
        ) : error ? (
          <ErrorState
            title={t('security.failedToLoadSettings')}
            description={error}
            onRetry={loadSettings}
          />
        ) : settings && (
          <div className="space-y-4">
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium text-foreground">{t('security.emailOnNewLogin')}</div>
                <div className="text-sm text-muted-foreground">{t('security.emailOnNewLoginDesc')}</div>
              </div>
              <Checkbox id="email_on_new_login" checked={settings.email_on_new_login} onChange={() => handleToggle('email_on_new_login')} />
            </div>
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium text-foreground">{t('security.require2FAForSensitive')}</div>
                <div className="text-sm text-muted-foreground">{t('security.require2FAForSensitiveDesc')}</div>
              </div>
              <Checkbox id="require_mfa_for_sensitive" checked={settings.require_mfa_for_sensitive} onChange={() => handleToggle('require_mfa_for_sensitive')} />
            </div>
            <div className="flex items-start justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium text-foreground">{t('security.rememberTrustedDevices')}</div>
                <div className="text-sm text-muted-foreground">{t('security.rememberTrustedDevicesDesc')}</div>
              </div>
              <div className="min-w-[220px]">
                <Select
                  id="trust_device_days"
                  value={String(settings.trust_device_days ?? 30)}
                  onChange={async (val) => {
                    const days = parseInt(val, 10)
                    const prev = settings
                    setSettings({ ...settings, trust_device_days: days })
                    setSaving(true)
                    try {
                      await performUpdate({ trust_device_days: days })
                    } catch (e) {
                      setSettings(prev)
                    } finally {
                      setSaving(false)
                    }
                  }}
                  options={[
                    { value: '0', label: t('security.doNotRememberDevices') },
                    { value: '30', label: t('security.days', { count: 30 }) },
                    { value: '60', label: t('security.days', { count: 60 }) },
                    { value: '90', label: t('security.days', { count: 90 }) },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </FormSection>

      <FormSection
        title={t('security.trustedDevices')}
      >
        {loadingDevices ? (
          <div className="py-6"><LoadingSpinner /></div>
        ) : devicesError ? (
          <ErrorState
            title={t('security.failedToLoadDevices')}
            description={devicesError}
            onRetry={loadDevices}
          />
        ) : (
          <>
            <DataTable
              data={devices}
              columns={columns as any}
              sorting={[]}
              onSortingChange={() => {}}
              enablePagination={false}
              isLoading={false}
              emptyMessage={t('security.noTrustedDevices')}
              emptyIcon={Monitor}
            />
            {/* Inside this branch: it described the list above it, so below a
                load failure it explained a table that was not there. */}
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> {t('security.devicesRemainTrusted')}
            </div>
          </>
        )}
      </FormSection>

      {/* Confirm Password Modal for sensitive actions */}
      <ConfirmPasswordModal
        isOpen={confirmModalOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmPassword}
        error={confirmModalError}
      />
    </div>
  )
}
